import { cn } from "../../lib/utils";
import { motion } from "framer-motion";
import { SECTIONS, useDeck } from "../../lib/deck-context";

export const STATUS = {
  verified: { label: "Verified", cls: "st-verified" },
  preliminary: { label: "Preliminary", cls: "st-preliminary" },
  target: { label: "Target", cls: "st-target" },
  tbv: { label: "To Be Verified", cls: "st-tbv" },
  nyc: { label: "Not Yet Committed", cls: "st-nyc" },
};

export const StatusBadge = () => null;

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`font-mono text-[11px] uppercase tracking-[0.22em] text-steel ${className}`}>{children}</div>
);

export const Reveal = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

export const SectionHeader = ({ eyebrow, title, lede, status }) => {
  const { idx } = useDeck();
  const num = SECTIONS[idx].num;
  return (
  <Reveal className="mb-12 max-w-4xl">
    <div className="flex items-center gap-4 mb-5">
      <span className="font-mono text-xs text-arq tracking-[0.2em]">{num}</span>
      <span className="h-px w-10 bg-arq/50" />
      <Eyebrow>{eyebrow}</Eyebrow>
      {status && <StatusBadge s={status} />}
    </div>
    <h1 data-testid="section-title" className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
      {title}
    </h1>
    {lede && <p className="mt-6 text-base md:text-lg text-char/80 leading-relaxed max-w-3xl">{lede}</p>}
  </Reveal>
  );
};

export const Card = ({ children, className = "", ...rest }) => (
  <div className={cn("bg-white border border-line rounded-[14px] shadow-[0_1px_2px_rgba(15,34,53,0.04),0_8px_24px_-12px_rgba(15,34,53,0.08)]", className)} {...rest}>
    {children}
  </div>
);

export const Metric = ({ label, value, sub, status, primary, testid, delay = 0 }) => (
  <Reveal delay={delay}>
    <Card data-testid={testid} className={`p-7 h-full metric-card ${primary ? "ring-1 ring-arq/40" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <Eyebrow>{label}</Eyebrow>
        {status && <StatusBadge s={status} />}
      </div>
      <div className={`mt-6 font-display text-4xl lg:text-[44px] leading-none tabular-nums ${primary ? "text-arq" : "text-ink"}`}>{value}</div>
      {sub && <div className="mt-4 text-sm text-steel">{sub}</div>}
    </Card>
  </Reveal>
);

export const Note = ({ children, testid, tone = "neutral", className = "" }) => (
  <div
    data-testid={testid}
    className={`flex gap-4 rounded-[12px] border px-6 py-5 text-sm leading-relaxed ${
      tone === "accent" ? "border-arq/30 bg-arq-soft text-ink" : tone === "dark" ? "border-ink bg-ink text-white/90" : "border-line bg-paper text-char"
    } ${className}`}
  >
    <span className={`mt-1 h-auto w-[3px] shrink-0 rounded-full ${tone === "dark" ? "bg-arq-mid" : "bg-arq"}`} />
    <div>{children}</div>
  </div>
);

export const Section = ({ children, testid }) => (
  <section data-testid={testid} className="px-6 sm:px-10 lg:px-16 py-12 lg:py-16 max-w-[1440px] mx-auto">
    {children}
  </section>
);

export const Scale = ({ value, max = 5, testid }) => (
  <div data-testid={testid} className="flex gap-1.5">
    {Array.from({ length: max }).map((_, i) => (
      <span key={i} className={`h-1.5 w-7 rounded-full transition-colors duration-300 ${i < value ? "bg-arq" : "bg-line"}`} />
    ))}
  </div>
);
